import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Check, Building2, BedDouble, Users, ClipboardList, Plus, Trash2 } from 'lucide-react';
import ShelterCareConnectLogo from './ui/logo';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const STEPS = [
  { key: 'profile', label: 'Shelter Profile', icon: Building2 },
  { key: 'rooms', label: 'Beds & Rooms', icon: BedDouble },
  { key: 'staff', label: 'Staff Accounts', icon: Users },
  { key: 'policies', label: 'Policies', icon: ClipboardList },
];

const Onboarding = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [profile, setProfile] = useState({ name: '', address: '', city: '', state: '', zip_code: '', phone: '', bed_count: '' });
  const [rooms, setRooms] = useState([{ room_number: '', capacity: '2' }]);
  const [staff, setStaff] = useState([{ full_name: '', username: '', password: '', role: 'general_staff' }]);
  const [policies, setPolicies] = useState({ max_stay_days: 45, gap_days: 30, house_rules: '' });

  useEffect(() => {
    axios.get(`${API}/shelter`).then((r) => {
      const s = r.data;
      if (s.onboarding_completed) { navigate('/dashboard'); return; }
      setProfile((p) => ({ ...p, name: s.name || '', bed_count: s.bed_count || '' }));
    }).catch(() => {});
  }, [navigate]);

  if (!user) return <Navigate to="/login" />;

  const saveProfile = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/shelter`, { ...profile, bed_count: profile.bed_count ? parseInt(profile.bed_count) : null });
      setStep(1);
    } catch (e) { toast.error('Could not save profile'); } finally { setBusy(false); }
  };

  const saveRooms = async () => {
    setBusy(true);
    try {
      const valid = rooms.filter((r) => r.room_number.trim());
      for (const r of valid) {
        await axios.post(`${API}/rooms`, { room_number: r.room_number.trim(), capacity: parseInt(r.capacity) || 1 });
      }
      if (valid.length) toast.success(`${valid.length} room(s) added`);
      setStep(2);
    } catch (e) { toast.error('Could not save rooms'); } finally { setBusy(false); }
  };

  const saveStaff = async () => {
    setBusy(true);
    try {
      const valid = staff.filter((s) => s.username.trim() && s.password.trim());
      for (const s of valid) {
        await axios.post(`${API}/auth/register`, {
          username: s.username.trim(), password: s.password, full_name: s.full_name.trim() || s.username.trim(), role: s.role
        });
      }
      if (valid.length) toast.success(`${valid.length} staff account(s) created`);
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create staff'); } finally { setBusy(false); }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/shelter`, { policies: { ...policies, max_stay_days: parseInt(policies.max_stay_days), gap_days: parseInt(policies.gap_days) } });
      await axios.post(`${API}/shelter/complete-onboarding`);
      toast.success('Your shelter is ready!');
      navigate('/dashboard');
    } catch (e) { toast.error('Could not complete onboarding'); } finally { setBusy(false); }
  };

  const StepIcon = STEPS[step].icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-10 px-4" data-testid="onboarding-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <ShelterCareConnectLogo size="md" showText={true} />
          <button onClick={() => { logout(); navigate('/login'); }} className="text-sm text-gray-500 hover:underline">Sign out</button>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex-1 flex items-center">
              <div className={`flex items-center gap-2 ${i <= step ? 'text-indigo-600' : 'text-gray-400'}`}>
                <div className={`h-8 w-8 rounded-full flex items-center justify-center border-2 ${i < step ? 'bg-indigo-600 border-indigo-600 text-white' : i === step ? 'border-indigo-600' : 'border-gray-300'}`}>
                  {i < step ? <Check className="h-4 w-4" /> : <s.icon className="h-4 w-4" />}
                </div>
                <span className="text-sm font-medium hidden sm:block">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < step ? 'bg-indigo-600' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        <Card className="border-0 card-shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><StepIcon className="h-5 w-5 text-indigo-600" /> {STEPS[step].label}</CardTitle>
            <CardDescription>Step {step + 1} of {STEPS.length}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 0 && (
              <div className="space-y-3">
                <Input placeholder="Shelter name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="ob-shelter-name" />
                <Input placeholder="Street address" value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} data-testid="ob-address" />
                <div className="grid grid-cols-3 gap-3">
                  <Input placeholder="City" value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} data-testid="ob-city" />
                  <Input placeholder="State" value={profile.state} onChange={(e) => setProfile({ ...profile, state: e.target.value })} data-testid="ob-state" />
                  <Input placeholder="ZIP" value={profile.zip_code} onChange={(e) => setProfile({ ...profile, zip_code: e.target.value })} data-testid="ob-zip" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="ob-phone" />
                  <Input type="number" placeholder="Total beds" value={profile.bed_count} onChange={(e) => setProfile({ ...profile, bed_count: e.target.value })} data-testid="ob-bed-count" />
                </div>
                <div className="flex justify-end">
                  <Button onClick={saveProfile} disabled={busy || !profile.name} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ob-next-0">Continue</Button>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-3">
                {rooms.map((r, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input placeholder="Room number/name" value={r.room_number} onChange={(e) => { const n = [...rooms]; n[i].room_number = e.target.value; setRooms(n); }} data-testid={`ob-room-number-${i}`} />
                    <Input type="number" className="w-28" placeholder="Beds" value={r.capacity} onChange={(e) => { const n = [...rooms]; n[i].capacity = e.target.value; setRooms(n); }} data-testid={`ob-room-capacity-${i}`} />
                    {rooms.length > 1 && <Button variant="ghost" size="sm" onClick={() => setRooms(rooms.filter((_, x) => x !== i))}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setRooms([...rooms, { room_number: '', capacity: '2' }])} data-testid="ob-add-room"><Plus className="h-4 w-4 mr-1" /> Add room</Button>
                <div className="flex justify-between pt-2">
                  <Button variant="ghost" onClick={() => setStep(0)}>Back</Button>
                  <Button onClick={saveRooms} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ob-next-1">Continue</Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                {staff.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-3" placeholder="Full name" value={s.full_name} onChange={(e) => { const n = [...staff]; n[i].full_name = e.target.value; setStaff(n); }} data-testid={`ob-staff-name-${i}`} />
                    <Input className="col-span-3" placeholder="Username" value={s.username} onChange={(e) => { const n = [...staff]; n[i].username = e.target.value; setStaff(n); }} data-testid={`ob-staff-username-${i}`} />
                    <Input className="col-span-3" type="password" placeholder="Password" value={s.password} onChange={(e) => { const n = [...staff]; n[i].password = e.target.value; setStaff(n); }} data-testid={`ob-staff-password-${i}`} />
                    <div className="col-span-2">
                      <Select value={s.role} onValueChange={(v) => { const n = [...staff]; n[i].role = v; setStaff(n); }}>
                        <SelectTrigger data-testid={`ob-staff-role-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general_staff">General Staff</SelectItem>
                          <SelectItem value="case_worker">Case Worker</SelectItem>
                          <SelectItem value="staff">Staff</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {staff.length > 1 && <Button variant="ghost" size="sm" className="col-span-1" onClick={() => setStaff(staff.filter((_, x) => x !== i))}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setStaff([...staff, { full_name: '', username: '', password: '', role: 'general_staff' }])} data-testid="ob-add-staff"><Plus className="h-4 w-4 mr-1" /> Add staff</Button>
                <div className="flex justify-between pt-2">
                  <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
                  <Button onClick={saveStaff} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ob-next-2">Continue</Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-gray-600">Max stay (days)</label>
                    <Input type="number" value={policies.max_stay_days} onChange={(e) => setPolicies({ ...policies, max_stay_days: e.target.value })} data-testid="ob-max-stay" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Gap before re-entry (days)</label>
                    <Input type="number" value={policies.gap_days} onChange={(e) => setPolicies({ ...policies, gap_days: e.target.value })} data-testid="ob-gap-days" />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">House rules (optional)</label>
                  <Textarea rows={4} value={policies.house_rules} onChange={(e) => setPolicies({ ...policies, house_rules: e.target.value })} data-testid="ob-house-rules" />
                </div>
                <div className="flex justify-between pt-2">
                  <Button variant="ghost" onClick={() => setStep(2)}>Back</Button>
                  <Button onClick={finish} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ob-finish">Finish setup</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Onboarding;
